import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import Container from "../../../components/Container";
import AlertMsg from "../../../components/AlertMsg";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";

// Import API hooks and utilities
import { giftCardSettingsService } from "../../../api/useQueryHooks";
import { QUERY_KEYS } from "../../../api/QueryKeys";

// Import constants, schemas, and utils
import { DEFAULT_GIFT_CARD_SETTINGS } from "./constants";
import { giftCardSettingsSchema } from "./schemas";

const GiftCardSettings = () => {
  const navigate = useNavigate();
  const { rest_id: restaurantId } = useParams();
  const queryClient = useQueryClient();

  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  // Form setup with React Hook Form and Zod validation
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(giftCardSettingsSchema),
    mode: "onChange",
    defaultValues: DEFAULT_GIFT_CARD_SETTINGS,
  });

  // Query to fetch gift card settings
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = giftCardSettingsService.useGetGiftCardSettings(restaurantId);

  // Mutation to update gift card settings
  const {
    mutate: updateSettings,
    isPending: isUpdating,
  } = giftCardSettingsService.useUpdateGiftCardSettings(
    (response) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.giftCardSettings, restaurantId],
      });
      setMsgAlert({
        open: true,
        message: response.msg || "Gift Card Settings updated successfully!",
        msgType: "success",
      });
      reset(undefined, { keepValues: true });
    },
    (error) => {
      setMsgAlert({
        open: true,
        message: error.message || "Failed to update Gift Card Settings",
        msgType: "error",
      });
    }
  );

  // Reset form when data is loaded
  useEffect(() => {
    if (settings) {
      // Wrap the settings in giftCardOptions if they come directly from API
      const formData = {
        giftCardOptions: settings
      };
      reset(formData);
    } else {
      reset(DEFAULT_GIFT_CARD_SETTINGS);
    }
  }, [settings, reset]);

  // Handle settings error
  useEffect(() => {
    if (settingsError) {
      setMsgAlert({
        open: true,
        message: settingsError.message || "Failed to load Gift Card Settings",
        msgType: "error",
      });
    }
  }, [settingsError]);

  // Handle form submission
  const onSubmit = (data) => {
    updateSettings({
      restaurantId,
      data: data.giftCardOptions,
    });
  };

  const handleCloseAlert = () => setMsgAlert({ open: false, message: "", msgType: "error" });

  const isLoading = isLoadingSettings || isUpdating;

  return (
    <Container page={"restaurants"} searchVal={""} onSearch={() => null} searchLabel="">
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Gift Card Settings"}
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
            />
          </Grid>
          <Grid item xs={12} px={3} pt={2}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Gift Card Options
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center">
                    <Controller
                      name="giftCardOptions.isPurchaseEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <Typography sx={{ ml: 2 }}>Enable Gift Card Purchase</Typography>
                  </Box>
                  {errors.giftCardOptions?.isPurchaseEnabled && (
                    <Typography variant="caption" color="error" sx={{ ml: 6 }}>
                      {errors.giftCardOptions.isPurchaseEnabled.message}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center">
                    <Controller
                      name="giftCardOptions.isRedemptionEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <Typography sx={{ ml: 2 }}>Enable Gift Card Redemption</Typography>
                  </Box>
                  {errors.giftCardOptions?.isRedemptionEnabled && (
                    <Typography variant="caption" color="error" sx={{ ml: 6 }}>
                      {errors.giftCardOptions.isRedemptionEnabled.message}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating}
                sx={{
                  backgroundColor: "#000",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#333" },
                  "&:disabled": { backgroundColor: "#000", color: "#fff" },
                }}
              >
                {isUpdating ? "Saving..." : "Save Gift Card Settings"}
              </Button>
            </form>
          </Grid>
        </Grid>
      )}

      <AlertMsg msgAlert={msgAlert} onCloseAlertMsg={handleCloseAlert} />
    </Container>
  );
};

export default GiftCardSettings;
